var _class;
import "core-js/modules/es.array.reverse.js";
import "core-js/modules/web.dom-collections.iterator.js";
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
import i18next from "i18next";
import { action, computed, observable, makeObservable } from "mobx";
import filterOutUndefined from "../Core/filterOutUndefined";
import Result from "../Core/Result";
import TerriaError, { TerriaErrorSeverity } from "../Core/TerriaError";
import CatalogMemberMixin, { getName } from "../ModelMixins/CatalogMemberMixin";
import ChartableMixin from "../ModelMixins/ChartableMixin";
import MappableMixin from "../ModelMixins/MappableMixin";
import ReferenceMixin from "../ModelMixins/ReferenceMixin";
import TimeFilterMixin from "../ModelMixins/TimeFilterMixin";
import LayerOrderingTraits from "../Traits/TraitsClasses/LayerOrderingTraits";
import CommonStrata from "./Definition/CommonStrata";
import hasTraits from "./Definition/hasTraits";
import MappableTraits from "../Traits/TraitsClasses/MappableTraits";
const keepOnTop = model => hasTraits(model, LayerOrderingTraits, "keepOnTop") && model.keepOnTop;
const supportsReordering = model => hasTraits(model, LayerOrderingTraits, "supportsReordering") && model.supportsReordering;
let Workbench = (_class = class Workbench {
  constructor() {
    _defineProperty(this, "_items", observable.array());
    makeObservable(this);
  }

  /**
   * Gets or sets the list of items on the workbench.
   */
  get items() {
    return this._items.map(dereferenceModel);
  }
  set items(items) {
    // Run items through a set to remove duplicates.
    const setItems = new Set(items);
    this._items.spliceWithArray(0, this._items.length, Array.from(setItems).slice());
  }

  /**
   * Gets the unique IDs of the items in the workbench.
   */
  get itemIds() {
    return filterOutUndefined(this._items.map(item => item.uniqueId));
  }

  /**
   * Gets the unique IDs of the items in the workbench.
   */
  get shouldExpandAll() {
    return this.items.every(item => !item.isOpenInWorkbench);
  }

  /**
   * Checks if the workbench contains time-based WMS
   */
  get hasTimeWMS() {
    return this._items.some(item => {
      var _item$discreteTimesAs;
      return item.type === "wms" && TimeFilterMixin.isMixedInto(item) && ((_item$discreteTimesAs = item.discreteTimesAsSortedJulianDates) === null || _item$discreteTimesAs === void 0 ? void 0 : _item$discreteTimesAs.length);
    });
  }

  /**
   * Removes a model or its dereferenced equivalent from the workbench.
   * @param item The model.
   */
  remove(item) {
    const index = this.indexOf(item);
    if (index >= 0) {
      this._items.splice(index, 1);
    }
  }

  /**
   * Removes all models from the workbench.
   */
  removeAll() {
    this._items.clear();
  }

  /**
   * Collapses all models from the workbench.
   */
  collapseAll() {
    this.items.map(item => {
      item.setTrait(CommonStrata.user, "isOpenInWorkbench", false);
    });
  }

  /**
   * Expands all models from the workbench.
   */
  expandAll() {
    this.items.map(item => {
      item.setTrait(CommonStrata.user, "isOpenInWorkbench", true);
    });
  }

  /**
   * Disable all items in the workbench.
   */
  disableAll() {
    this.items.forEach(item => {
      if (hasTraits(item, MappableTraits, "show")) {
        item.setTrait(CommonStrata.user, "show", false);
      }
    });
  }
  enableAll() {
    this.items.forEach(item => {
      if (hasTraits(item, MappableTraits, "show")) {
        item.setTrait(CommonStrata.user, "show", true);
      }
    });
  }

  /**
   * Adds an item to the workbench. If the item is already present, this method does nothing.
   * Note that the model's dereferenced equivalent may appear in the {@link Workbench#items} list
   * rather than the model itself.
   * @param item The model to add.
   */
  insertItem(item) {
    let index = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    if (this.contains(item)) {
      return;
    }
    const targetItem = dereferenceModel(item);

    // Keep reorderable data sources (e.g.: imagery layers) below non-orderable ones (e.g.: GeoJSON).
    if (supportsReordering(targetItem)) {
      while (index < this.items.length && !supportsReordering(this.items[index])) {
        ++index;
      }
    } else {
      while (index > 0 && this.items.length > 0 && supportsReordering(this.items[index - 1])) {
        --index;
      }
    }
    if (!keepOnTop(targetItem)) {
      while (index < this.items.length && keepOnTop(this.items[index]) && supportsReordering(this.items[index]) === supportsReordering(targetItem)) {
        ++index;
      }
    } else {
      while (index > 0 && this.items.length > 0 && !keepOnTop(this.items[index - 1]) && supportsReordering(this.items[index - 1]) === supportsReordering(targetItem)) {
        --index;
      }
    }

    // Make sure the reference, rather than the target, is added to the items list.
    const referenceItem = item.sourceReference ? item.sourceReference : item;
    this._items.splice(index, 0, referenceItem);
  }

  /**
   * Adds or removes a model to/from the workbench. If the model is a reference,
   * it will also be dereferenced. If, after dereferencing, the item turns out not to
   * be {@link AsyncMappableMixin} or {@link ChartableMixin} but it is a {@link GroupMixin}, it will
   * be removed from the workbench. If it is mappable, `loadMapItems` will be called.
   *
   * If an error occurs, it will only be added to the workbench if the severity is TerriaError.Warning - otherwise it will not be added
   *
   * @param item The item to add to or remove from the workbench.
   */
  async add(item) {
    var _error;
    if (Array.isArray(item)) {
      const results = await Promise.all(item.reverse().map(i => this.add(i)));
      return Result.combine(results, {
        title: i18next.t("workbench.addItemErrorTitle"),
        message: i18next.t("workbench.addItemErrorMessage"),
        importance: -1
      });
    }
    if (MappableMixin.isMixedInto(item) && item.shouldShowInitialMessage) {
      await item.showInitialMessage();
    }
    this.insertItem(item);
    let error;
    if (ReferenceMixin.isMixedInto(item)) {
      error = (await item.loadReference()).error;
      if (item.target) {
        this.remove(item);
        return this.add(item.target);
      }
    }

    // Add warning message if item isn't mappable or chartable
    if (!error && !MappableMixin.isMixedInto(item) && !ChartableMixin.isMixedInto(item)) {
      error = TerriaError.from("".concat(getName(item), " doesn't have anything to visualize"), TerriaErrorSeverity.Warning);
    }
    if (!error && CatalogMemberMixin.isMixedInto(item)) error = (await item.loadMetadata()).error;
    if (!error && MappableMixin.isMixedInto(item)) {
      error = (await item.loadMapItems()).error;
      if (!error && item.zoomOnAddToWorkbench && !item.disableZoomTo) {
        item.terria.currentViewer.zoomTo(item);
      }
    }

    // Remove item if TerriaError severity is Error
    if (((_error = error) === null || _error === void 0 ? void 0 : _error.severity) === TerriaErrorSeverity.Error) {
      this.remove(item);
    }
    return Result.none(error, {
      title: i18next.t("workbench.addItemErrorTitle"),
      message: i18next.t("workbench.addItemErrorMessage"),
      importance: -1
    });
  }

  /**
   * Determines if a given model or its dereferenced equivalent exists in the workbench list.
   * @param item The model.
   * @returns True if the model or its dereferenced equivalent exists on the workbench; otherwise, false.
   */
  contains(item) {
    return this.indexOf(item) >= 0;
  }

  /**
   * Returns the index of a given model or its dereferenced equivalent in the workbench list.
   * @param item The model.
   * @returns The index of the model or its dereferenced equivalent, or -1 if neither exist on the workbench.
   */
  indexOf(item) {
    return this.items.findIndex(model => model === item || dereferenceModel(model) === dereferenceModel(item));
  }

  /**
   * Used to re-order the workbench list.
   * @param item The model to be moved.
   * @param newIndex The new index to shift the model to.
   */
  moveItemToIndex(item, newIndex) {
    if (!this.contains(item)) {
      return;
    }
    this.remove(item);
    this.insertItem(item, newIndex);
  }
}, _applyDecoratedDescriptor(_class.prototype, "items", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "items"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "itemIds", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "itemIds"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "shouldExpandAll", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "shouldExpandAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "hasTimeWMS", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "hasTimeWMS"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "remove", [action], Object.getOwnPropertyDescriptor(_class.prototype, "remove"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "removeAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "removeAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "collapseAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "collapseAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "expandAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "expandAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "disableAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "disableAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "enableAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "enableAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "insertItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "insertItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "moveItemToIndex", [action], Object.getOwnPropertyDescriptor(_class.prototype, "moveItemToIndex"), _class.prototype), _class);
export { Workbench as default };
function dereferenceModel(model) {
  if (ReferenceMixin.isMixedInto(model) && model.target !== undefined) {
    return model.target;
  }
  return model;
}